import { useEffect } from 'react';
import { useSEO } from '../../../utils/seo';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';
import WhatsAppButton from '../../home/components/WhatsAppButton';

export default function MotorRektifiyePage() {
  useSEO({
    title: 'Motor Rektifiye ve Mekanik Tamir | Denizli Araç Servisi',
    description: 'Denizli\'de profesyonel motor rektifiye ve mekanik tamir hizmetleri. Üst segment araçlarda uzman kadro, son teknoloji ekipmanlar. Mercedes, BMW, Audi motor revizyonu ve onarımı.',
    keywords: 'denizli motor rektifiye, denizli motor tamiri, denizli motor revizyonu, denizli mekanik tamir, denizli motor bakımı, mercedes motor tamiri denizli',
    canonical: '/blog/motor-rektifiye-mekanik-tamir',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: 'Motor Rektifiye & Mekanik Tamir',
      description: 'Uzman kadromuzla motor revizyonu ve tüm mekanik işlemler. Üst segment araçlarda özel uzmanlık.',
      author: {
        '@type': 'Organization',
        name: 'D-Atolye Premium Otomotiv Servisi',
      },
      publisher: {
        '@type': 'Organization',
        name: 'D-Atolye Premium Otomotiv Servisi',
        logo: {
          '@type': 'ImageObject',
          url: 'https://static.readdy.ai/image/5946f8dc6d1689a139aced3671fb829b/5627d08f05dce4eda6468c71266b611d.png',
        },
      },
      datePublished: '2024-01-15',
      dateModified: new Date().toISOString().split('T')[0],
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/blog/motor-rektifiye-mekanik-tamir`,
      },
    },
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative h-[500px] flex items-center justify-center overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/3113465b0063a1f47dc07ff551888736.jpg"
          alt="Motor Rektifiye"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60"></div>
        <div className="relative z-10 text-center text-white px-6 max-w-4xl mx-auto">
          <h1 className="font-serif text-5xl lg:text-6xl font-bold mb-6">Motor Rektifiye &amp; Mekanik Tamir</h1>
          <p className="font-sans text-xl text-gray-200">Uzman kadromuzla motor revizyonu ve tüm mekanik işlemler</p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          
          {/* Introduction */}
          <div className="mb-16">
            <p className="font-sans text-lg text-gray-700 leading-relaxed mb-6">
              Motor, aracınızın kalbidir ve düzenli bakım ile doğru onarım gerektiren en kritik bileşendir. Servisimiz, özellikle üst segment araçlarda uzmanlaşmış kadrosuyla, motor rektifiye, revizyon ve tüm mekanik tamir işlemlerini en yüksek standartlarda gerçekleştirir. 15 yıllık deneyimimiz ve son teknoloji ekipmanlarımızla, motorunuzun performansını ve ömrünü maksimize ediyoruz.
            </p>
          </div>

          {/* Motor Rektifiye Nedir? */}
          <div className="mb-16">
            <h2 className="font-serif text-3xl font-bold text-gray-900 mb-6">Motor Rektifiye Nedir?</h2>
            <p className="font-sans text-base text-gray-700 leading-relaxed mb-4">
              Motor rektifiye, motorun aşınmış veya hasarlı parçalarının yenilenmesi, silindir bloğunun taşlanması ve pistonların değiştirilmesi işlemidir. Bu kapsamlı işlem, motorunuza yeni gibi performans kazandırır. Motor rektifiye gerektiğinde ortaya çıkan belirtiler:
            </p>
            <ul className="space-y-3 ml-6">
              <li className="font-sans text-base text-gray-700 flex items-start">
                <i className="ri-alert-line text-accent-gold text-xl mr-3 mt-1"></i>
                <span>Aşırı yağ tüketimi ve egzozdan mavi duman çıkması</span>
              </li>
              <li className="font-sans text-base text-gray-700 flex items-start">
                <i className="ri-alert-line text-accent-gold text-xl mr-3 mt-1"></i>
                <span>Motor gücünde belirgin düşüş ve performans kaybı</span>
              </li>
              <li className="font-sans text-base text-gray-700 flex items-start">
                <i className="ri-alert-line text-accent-gold text-xl mr-3 mt-1"></i>
                <span>Anormal motor sesleri ve titreşimler</span>
              </li>
              <li className="font-sans text-base text-gray-700 flex items-start">
                <i className="ri-alert-line text-accent-gold text-xl mr-3 mt-1"></i>
                <span>Kompresyon kaybı ve zor çalışma</span>
              </li>
              <li className="font-sans text-base text-gray-700 flex items-start">
                <i className="ri-alert-line text-accent-gold text-xl mr-3 mt-1"></i>
                <span>Yüksek kilometre ve yaşlı motorlarda genel aşınma</span>
              </li>
            </ul>
          </div>

          {/* Image */}
          <div className="mb-16 rounded-2xl overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=detailed%20automotive%20engine%20components%20laid%20out%20on%20clean%20workbench%2C%20precision%20measurement%20tools%2C%20cylinder%20head%20and%20pistons%2C%20professional%20engine%20rebuild%20parts%2C%20technical%20workshop%20setting%2C%20organized%20mechanical%20parts&width=1200&height=600&seq=blog-motor-002&orientation=landscape"
              alt="Motor Parçaları"
              className="w-full h-96 object-cover"
            />
          </div>

          {/* Rektifiye Süreci */}
          <div className="mb-16">
            <h2 className="font-serif text-3xl font-bold text-gray-900 mb-6">Motor Rektifiye Sürecimiz</h2>
            <p className="font-sans text-base text-gray-700 leading-relaxed mb-6">
              Motor rektifiye, hassasiyet ve deneyim gerektiren karmaşık bir işlemdir. Sürecimiz şu aşamalardan oluşur:
            </p>
            
            <div className="space-y-8">
              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="font-sans text-xl font-semibold text-gray-900 mb-3 flex items-center">
                  <span className="bg-accent-gold text-white w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm">1</span>
                  Motor Sökümü ve İnceleme
                </h3>
                <p className="font-sans text-base text-gray-700 leading-relaxed ml-11">
                  Motor araçtan dikkatli bir şekilde sökülür ve tüm parçalar ayrıştırılır. Her parça detaylı olarak incelenir, ölçülür ve hasarlı veya aşınmış parçalar belirlenir. Silindir bloğu, krank mili, supaplar ve diğer kritik bileşenler mikrometrik hassasiyetle kontrol edilir.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="font-sans text-xl font-semibold text-gray-900 mb-3 flex items-center">
                  <span className="bg-accent-gold text-white w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm">2</span>
                  Taşlama ve Honlama İşlemleri
                </h3>
                <p className="font-sans text-base text-gray-700 leading-relaxed ml-11">
                  Silindir bloğu özel taşlama tezgahlarında işlenir. Silindirler, yeni piston ölçülerine göre hassas bir şekilde taşlanır ve honlanır. Krank mili ve kam mili de gerekirse taşlanarak düzeltilir. Bu işlemler, motorun orijinal toleranslarına geri dönmesini sağlar.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="font-sans text-xl font-semibold text-gray-900 mb-3 flex items-center">
                  <span className="bg-accent-gold text-white w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm">3</span>
                  Parça Değişimi ve Montaj
                </h3>
                <p className="font-sans text-base text-gray-700 leading-relaxed ml-11">
                  Orijinal veya OEM kalitesinde yeni parçalar kullanılır. Pistonlar, segmanlar, yatak keçeleri, conta takımı ve diğer sarf malzemeleri yenilenir. Motor, üretici spesifikasyonlarına uygun olarak özenle monte edilir. Tork değerleri hassas aletlerle kontrol edilir.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="font-sans text-xl font-semibold text-gray-900 mb-3 flex items-center">
                  <span className="bg-accent-gold text-white w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm">4</span>
                  Test ve Rodaj
                </h3>
                <p className="font-sans text-base text-gray-700 leading-relaxed ml-11">
                  Motor araca takıldıktan sonra, test standında çalıştırılır. Yağ basıncı, sıcaklık, kompresyon ve diğer parametreler kontrol edilir. Rodaj süreci hakkında detaylı bilgilendirme yapılır ve ilk 1000 km için özel bakım talimatları verilir.
                </p>
              </div>
            </div>
          </div>

          {/* Mekanik Tamir Hizmetleri */}
          <div className="mb-16">
            <h2 className="font-serif text-3xl font-bold text-gray-900 mb-6">Mekanik Tamir Hizmetlerimiz</h2>
            <p className="font-sans text-base text-gray-700 leading-relaxed mb-6">
              Motor rektifiyenin yanı sıra, tüm mekanik tamir ve bakım işlemlerini de gerçekleştiriyoruz:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="border border-gray-200 p-6 rounded-xl hover:border-accent-gold transition-colors">
                <i className="ri-settings-3-line text-3xl text-accent-gold mb-3"></i>
                <h4 className="font-sans text-lg font-semibold text-gray-900 mb-2">Silindir Kapağı Tamiri</h4>
                <p className="font-sans text-sm text-gray-600">Supap ayarı, conta değişimi, taşlama ve test işlemleri</p>
              </div>
              <div className="border border-gray-200 p-6 rounded-xl hover:border-accent-gold transition-colors">
                <i className="ri-oil-line text-3xl text-accent-gold mb-3"></i>
                <h4 className="font-sans text-lg font-semibold text-gray-900 mb-2">Yağ Pompası ve Sistem Bakımı</h4>
                <p className="font-sans text-sm text-gray-600">Yağlama sisteminin kontrolü ve onarımı</p>
              </div>
              <div className="border border-gray-200 p-6 rounded-xl hover:border-accent-gold transition-colors">
                <i className="ri-temp-cold-line text-3xl text-accent-gold mb-3"></i>
                <h4 className="font-sans text-lg font-semibold text-gray-900 mb-2">Soğutma Sistemi Tamiri</h4>
                <p className="font-sans text-sm text-gray-600">Radyatör, termostat, su pompası onarım ve değişimi</p>
              </div>
              <div className="border border-gray-200 p-6 rounded-xl hover:border-accent-gold transition-colors">
                <i className="ri-flashlight-line text-3xl text-accent-gold mb-3"></i>
                <h4 className="font-sans text-lg font-semibold text-gray-900 mb-2">Ateşleme Sistemi</h4>
                <p className="font-sans text-sm text-gray-600">Buji, bobin ve ateşleme kablosu kontrolü ve değişimi</p>
              </div>
              <div className="border border-gray-200 p-6 rounded-xl hover:border-accent-gold transition-colors">
                <i className="ri-gas-station-line text-3xl text-accent-gold mb-3"></i>
                <h4 className="font-sans text-lg font-semibold text-gray-900 mb-2">Yakıt Sistemi Bakımı</h4>
                <p className="font-sans text-sm text-gray-600">Enjektör temizliği, yakıt pompası ve filtre değişimi</p>
              </div>
              <div className="border border-gray-200 p-6 rounded-xl hover:border-accent-gold transition-colors">
                <i className="ri-steering-2-line text-3xl text-accent-gold mb-3"></i>
                <h4 className="font-sans text-lg font-semibold text-gray-900 mb-2">Triger Kayış Değişimi</h4>
                <p className="font-sans text-sm text-gray-600">Zamanında triger bakımı ve gergi rulman değişimi</p>
              </div>
            </div>
          </div>

          {/* Üst Segment Uzmanlığı */}
          <div className="mb-16 bg-primary-dark rounded-2xl p-10">
            <h2 className="font-serif text-3xl font-bold text-white mb-6">Üst Segment Araç Uzmanlığımız</h2>
            <p className="font-sans text-base text-gray-300 leading-relaxed mb-4">
              Mercedes, BMW, Audi, Porsche gibi premium markalarda özel uzmanlığımız bulunmaktadır. Bu araçların motorları, özel bilgi, deneyim ve ekipman gerektirir. Servisimizde:
            </p>
            <ul className="space-y-3 ml-6">
              <li className="font-sans text-base text-gray-300 flex items-start">
                <i className="ri-star-fill text-accent-gold text-xl mr-3 mt-1"></i>
                <span>Marka özel teşhis cihazları ve yazılımları kullanılır</span>
              </li>
              <li className="font-sans text-base text-gray-300 flex items-start">
                <i className="ri-star-fill text-accent-gold text-xl mr-3 mt-1"></i>
                <span>Orijinal parça ve OEM kalitesinde yedek parçalar tercih edilir</span>
              </li>
              <li className="font-sans text-base text-gray-300 flex items-start">
                <i className="ri-star-fill text-accent-gold text-xl mr-3 mt-1"></i>
                <span>Üretici spesifikasyonlarına tam uyum sağlanır</span>
              </li>
              <li className="font-sans text-base text-gray-300 flex items-start">
                <i className="ri-star-fill text-accent-gold text-xl mr-3 mt-1"></i>
                <span>Uzman teknisyenlerimiz düzenli eğitimlerle güncel kalır</span>
              </li>
            </ul>
          </div>

          {/* Garanti ve Güvenilirlik */}
          <div className="mb-16 bg-primary-dark rounded-2xl p-10">
            <h2 className="font-serif text-3xl font-bold text-white mb-6">Güvenilirlik ve Kalite Garantisi</h2>
            <p className="font-sans text-base text-gray-300 leading-relaxed mb-4">
              Motor rektifiye ve mekanik tamir hizmetlerimizde kalite ve güvenilirlik önceliğimizdir:
            </p>
            <ul className="space-y-3 ml-6">
              <li className="font-sans text-base text-gray-300 flex items-start">
                <i className="ri-shield-check-fill text-accent-gold text-xl mr-3 mt-1"></i>
                <span>Orijinal veya OEM parça kullanımı garantisi</span>
              </li>
              <li className="font-sans text-base text-gray-300 flex items-start">
                <i className="ri-shield-check-fill text-accent-gold text-xl mr-3 mt-1"></i>
                <span>Uzman teknisyen kadrosu</span>
              </li>
              <li className="font-sans text-base text-gray-300 flex items-start">
                <i className="ri-shield-check-fill text-accent-gold text-xl mr-3 mt-1"></i>
                <span>Son teknoloji ekipman ve ölçüm cihazları</span>
              </li>
              <li className="font-sans text-base text-gray-300 flex items-start">
                <i className="ri-shield-check-fill text-accent-gold text-xl mr-3 mt-1"></i>
                <span>Detaylı test ve kalite kontrol süreçleri</span>
              </li>
              <li className="font-sans text-base text-gray-300 flex items-start">
                <i className="ri-shield-check-fill text-accent-gold text-xl mr-3 mt-1"></i>
                <span>Tüm işlemler e-karneye kaydedilir</span>
              </li>
            </ul>
          </div>

          {/* CTA Section */}
          <div className="bg-gray-50 rounded-2xl p-10 text-center">
            <h3 className="font-serif text-3xl font-bold text-gray-900 mb-4">Motorunuz Sorun mu Yaşıyor?</h3>
            <p className="font-sans text-base text-gray-700 mb-8 max-w-2xl mx-auto">
              Ücretsiz motor kontrolü için hemen randevu alın. Uzman kadromuz aracınızı detaylı olarak inceleyip en doğru çözümü sunacaktır.
            </p>
            <a
              href="/#appointment"
              className="inline-block bg-accent-gold text-primary-dark px-8 py-4 rounded-lg font-sans font-semibold hover:bg-primary-dark hover:text-white transition-colors whitespace-nowrap cursor-pointer"
            >
              Ücretsiz Kontrol Randevusu
            </a>
          </div>

        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
}